import { type InputHTMLAttributes, type JSX, useId } from 'react';

import { cn } from '../lib/cn';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  label: string;
  hint?: string;
  error?: string;
  indeterminate?: boolean;
}

/**
 * Same shape as `Field` (label via `useId`, `error`/`hint`) applied to a
 * checkbox. `indeterminate` has no HTML attribute \u2014 it's set on the DOM
 * node directly via a ref callback, independently of `checked`.
 */
export function Checkbox({
  label,
  hint,
  error,
  indeterminate = false,
  className,
  required,
  ...props
}: CheckboxProps): JSX.Element {
  const inputId = useId();
  const messageId = `${inputId}-message`;
  const message = error ?? hint;
  const hasError = error !== undefined;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <input
          id={inputId}
          type="checkbox"
          ref={(node) => {
            if (node !== null) node.indeterminate = indeterminate;
          }}
          required={required}
          aria-invalid={hasError}
          aria-describedby={message === undefined ? undefined : messageId}
          className={cn(
            'size-5 rounded border-slate-300 accent-primary-600',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
            hasError
              ? 'border-red-500 focus-visible:ring-red-500'
              : 'focus-visible:ring-primary-500',
            className,
          )}
          {...props}
        />
        <label htmlFor={inputId} className="text-sm font-medium text-slate-900">
          {label}
          {required === true ? (
            <span aria-hidden="true" className="ml-0.5 text-red-600">
              *
            </span>
          ) : null}
        </label>
      </div>

      {message === undefined ? null : (
        <p
          id={messageId}
          role={hasError ? 'alert' : undefined}
          className={cn('text-xs', hasError ? 'text-red-600' : 'text-slate-500')}
        >
          {message}
        </p>
      )}
    </div>
  );
}
